import { useMemo } from 'react';
import styled from 'styled-components';
import SubRTableView from '../../SubRTableView';
import { calculateTotalTableData } from '../../../helpers/utils';
import { formatDateWithTimezoneOffset } from '../../../../../helpers/utils';
import type { PurchaseValueSubRData } from '../../../types/inventorySubRTypes';
import NoDataUI from '../NoDataUI';
import OverviewSection from '../overviewSection';

interface ITableViewSectionProps {
  currentReportWidget: string;
  contentData: PurchaseValueSubRData;
  value: string;
}

const TableViewSection = ({ contentData, value }: ITableViewSectionProps) => {
  const transformedData = useMemo(() => {
    const rawData = contentData?.data ?? [];
    const config = contentData?.config;

    if (!rawData.length) return null;

    const currency = config?.currency_code ?? '';
    const currencyPrecision = config?.currency_precision ?? 2;

    const firstRow = rawData[0];
    const timezoneOffset = firstRow?.offset;
    const timestampValue = firstRow?.received_date;

    // Transform data by adding serial numbers
    const tableData = rawData.map((row: any, index: number) => ({
      ...row,
      sr_no: index + 1
    }));

    return {
      subHeading: {
        label: 'Location: ',
        value: firstRow.location || ''
      },
      overview: {
        details: [
          {
            label: 'Created By',
            value: firstRow.created_by || '-'
          },
          {
            label: 'Received By',
            value: firstRow.received_by || '-'
          },
          {
            label: 'Received Date',
            value: formatDateWithTimezoneOffset(
              timestampValue?.toString(),
              timezoneOffset,
              'dd MMM yyyy, hh:mm:ss aa'
            )
          },
          {
            label: 'Invoice/Ref No',
            value: firstRow.invoice_no || '-'
          }
        ],
        summary: {
          label: 'Comments',
          value: firstRow.comments || ''
        }
      },
      table: {
        columns: [
          {
            header: 'Sr. No',
            accessor: 'sr_no'
          },
          {
            header: 'Item',
            accessor: 'item'
          },
          {
            header: `Opening Balance`,
            accessor: 'opening_balance'
          },
          {
            header: 'Supplier',
            accessor: 'supplier'
          },
          {
            header: `Supplier Price (${currency}) /Unit`,
            accessor: 'cost_unit'
          },
          {
            header: 'Unit Value',
            accessor: 'unit_value'
          },
          {
            header: 'Ordered Quantity',
            accessor: 'ordered_quantity'
          },
          {
            header: 'Deliver To',
            accessor: 'deliver_to'
          },
          {
            header: 'Received Quantity',
            accessor: 'received_quantity'
          },
          {
            header: 'Closing Balance',
            accessor: 'closing_balance'
          },
          {
            header: `Total (${currency})`,
            accessor: 'total_amount'
          }
        ],
        data: tableData
      },
      total: {
        label: 'Total',
        value: calculateTotalTableData(rawData, 'total_amount', currencyPrecision)
      }
    };
  }, [contentData]);

  if (!transformedData) {
    return (
      <Wrapper>
        <NoDataUI />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div style={{ width: '100%', height: '100%' }}>
        <OverviewSection
          overviewData={transformedData.overview}
          location={transformedData.subHeading}
          reportId={value}
        />
        <SubRTableView
          data={{
            ...transformedData.table,
            config: contentData.config,
            total: transformedData.total
          }}
        />
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
`;

export default TableViewSection;
